// Web i18n — the operator-facing UI-locale layer.
//
// This is the UI-LOCALE axis (the language of the chrome the operator reads),
// deliberately SEPARATE from the per-project CONTENT-locale (the language issue
// prose is authored in, owned server-side). They share the `Locale` set but are
// independent: a German-speaking operator gets German UI regardless of which
// language a given project's issues are written in. The one crossing: a new
// session sends `currentUiLocale()`, and on a project whose `.loom.yml` sets no
// locale the agent is told to write in it by default — never the frame, the
// grounding or anything else the content locale keys.
//
// Design notes that keep the test suite green:
//   - `LocaleContext` defaults to `en`, so a component rendered WITHOUT a
//     `LocaleProvider` (most unit tests) resolves to English — existing
//     English-text assertions keep passing untouched.
//   - `useT()` returns a stable `t(key, vars?)` lookup over the typed catalog;
//     an unknown/asymmetric key falls back to English then to the key itself,
//     so a missing translation degrades to English, never throws.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { uiPublicName, withPublicName } from "@loom/shared/brand";
import { asLocale, type Locale } from "@loom/shared/config";

import { MESSAGES, type MessageKey, PLURALS, type PluralKey } from "./messages/index.js";
import { publicNameTemplate } from "./publicName.js";

export type { Locale };
export { MESSAGES, type MessageKey };

const STORAGE_KEY = "loom.ui.locale";

interface LocaleContextValue {
  readonly locale: Locale;
  readonly setLocale: (next: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: "en",
  setLocale: () => {},
});

/**
 * The host's language (BCP 47), when Focus runs as the panel inside an
 * assistant (`src/panel/`): its `hostContext.locale`, which says what the
 * user reads there better than the iframe's `navigator.language`. Set by the
 * panel's entry before render; the web never sets it.
 */
let hostLocaleHint: string | undefined;

export const setHostLocaleHint = (bcp47: string | undefined): void => {
  hostLocaleHint = bcp47;
};

/** Browser default → a supported UI locale (de for a German browser, else en).
 *  The panel's host language comes first; a stored choice still wins over both. */
const browserDefault = (): Locale => {
  const language =
    hostLocaleHint ?? (typeof navigator === "undefined" ? undefined : navigator.language);
  if (!language) return "en";
  return language.toLowerCase().startsWith("de") ? "de" : "en";
};

const readStored = (): Locale | null => {
  // The guard sits inside the try: where the browser blocks site data, merely
  // reading the `localStorage` global throws, and that throw — before React
  // mounted — left every page blank.
  try {
    if (typeof localStorage === "undefined") return null;
    const v = localStorage.getItem(STORAGE_KEY);
    return v ? asLocale(v) : null;
  } catch {
    return null;
  }
};

/**
 * The interface language the operator is looking at right now — the same
 * resolution `LocaleProvider` starts from (a stored choice, else the browser's
 * language). Read outside React, by the two non-component callers that need
 * it: `createSession` and every `POST /reconnect` (`reconnectBody`), which send
 * it so the agent's directive names this language when the project's
 * `.loom.yml` sets none. It crosses the axis on that one point only; the
 * content locale stays server-owned.
 */
export const currentUiLocale = (): Locale => readStored() ?? browserDefault();

export const LocaleProvider = ({
  children,
  initial,
}: {
  readonly children: ReactNode;
  /** Override the resolved initial locale (tests / SSR). */
  readonly initial?: Locale;
}): JSX.Element => {
  const [locale, setLocaleState] = useState<Locale>(
    () => initial ?? readStored() ?? browserDefault(),
  );

  const setLocale = useCallback((next: Locale): void => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable — keep the in-memory choice */
    }
  }, []);

  // Reflect the locale on <html lang> for assistive tech + correct hyphenation.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = (): LocaleContextValue => useContext(LocaleContext);

type Vars = Record<string, string | number>;

const interpolate = (template: string, vars?: Vars): string =>
  vars
    ? template.replace(/\{(\w+)\}/g, (_, k: string) =>
        k in vars ? String(vars[k]) : `{${k}}`,
      )
    : template;

/** Translate a catalog key for `locale`, with `{var}` interpolation. Falls back
 *  English → key so a gap degrades visibly-but-safely instead of throwing.
 *  Under the UI public-name mode (`./publicName.ts`) the template names the
 *  public name, swapped before the vars are filled in. */
export const translate = (
  locale: Locale,
  key: MessageKey,
  vars?: Vars,
): string => {
  const entry = MESSAGES[key];
  if (!entry) return key;
  const template = entry[locale] ?? entry.en ?? key;
  return interpolate(
    uiPublicName() ? publicNameTemplate(key, locale, template) : template,
    vars,
  );
};

export interface Translator {
  /** Single message lookup. */
  (key: MessageKey, vars?: Vars): string;
}

/** The component-facing hook. `t("readiness.accept")`. */
export const useT = (): Translator => {
  const { locale } = useLocale();
  return useCallback(
    (key: MessageKey, vars?: Vars) => translate(locale, key, vars),
    [locale],
  );
};

// ---------------------------------------------------------------------------
// Pluralization — Intl.PluralRules over the catalog's plural entries. Replaces
// the hand-rolled `n === 1 ? "" : "s"` ternaries (English-only grammar).
// ---------------------------------------------------------------------------

const pluralRules: Partial<Record<Locale, Intl.PluralRules>> = {};
const rulesFor = (locale: Locale): Intl.PluralRules =>
  (pluralRules[locale] ??= new Intl.PluralRules(locale));

/** Plural-aware lookup: `tn("focus.questions", 3)` → "3 Fragen". The catalog
 *  entry carries each CLDR category the locale needs (en/de use one|other). */
export const usePlural = (): ((key: PluralKey, count: number, vars?: Vars) => string) => {
  const { locale } = useLocale();
  return useCallback(
    (key: PluralKey, count: number, vars?: Vars) => {
      const entry = PLURALS[key];
      if (!entry) return key;
      const forms = entry[locale] ?? entry.en;
      const cat = rulesFor(locale).select(count) as keyof typeof forms;
      const template = forms[cat] ?? forms.other;
      // No plural form needs a hand-written line under the public-name mode.
      return interpolate(uiPublicName() ? withPublicName(template) : template, {
        count,
        ...vars,
      });
    },
    [locale],
  );
};
